import moment from 'moment/moment';
import React, { useMemo } from 'react';
import { DownOutlined } from '@ant-design/icons';
import { ChangeStatusMapping } from 'models/Task';
import { PERMISSIONS } from 'constants/permissions';
import { TaskIconGreen } from 'components/common/icons/TaskIconGreen';
import { StatusBadge, StatusType } from 'components/common/StatusBadge';
import { useAccessControl } from 'components/common/accessControl/useAccessControl';
import {
  Button,
  Col,
  Dropdown,
  EditableInfoComponent,
  HasAccess,
  MenuProps,
  PriorityTag,
  Row,
  Space,
  Typography,
} from 'components/index';
import { useTaskInfo } from './useTaskInfo';
import { TaskInfoEditModal } from './edit';
import styles from './styles.module.scss';

const { Text } = Typography;

interface ITaskInfo {
  entity:
    | 'BRAND'
    | 'CLIENT'
    | 'ADMIN'
    | 'TASK'
    | 'SUPPLIER'
    | 'LEAD'
    | 'DEAL'
    | 'ITEM'
    | 'COUNTRY'
    | 'PURCHASE_REQUEST'
    | 'PURCHASE_ORDER';
  id: string;
  isFinalStatus?: boolean;
}

export const TaskInfo = React.memo((props: ITaskInfo) => {
  const { id, entity, isFinalStatus } = props;
  const { hasAccess } = useAccessControl();
  const { handleStatusChange, taskDetails, onModalStateChange, modalState } = useTaskInfo({ id });
  const changeStatusMenu: MenuProps['items'] = useMemo(() => {
    const availableOptions = ChangeStatusMapping[taskDetails.status] || [];
    return availableOptions.map((i: { label: string; value: StatusType }) => ({
      label: <StatusBadge type={i.value} tooltip={i.label} />,
      key: i.value,
    }));
  }, [taskDetails]);
  return (
    <>
      <EditableInfoComponent
        title={'Task Details'}
        editable={hasAccess(PERMISSIONS.tasks.task.write) && !isFinalStatus}
        icon={<TaskIconGreen />}
        onEdite={onModalStateChange}
      />
      <Col>
        <Row gutter={[0, 20]}>
          <Col span={24}>
            <Row gutter={[0, 4]}>
              <Col span={24}>
                <Row justify={'space-between'}>
                  <Text type={'secondary'} className={styles.item_bold}>
                    Assign To
                  </Text>
                  <Text type={'secondary'} className={styles.item_bold}>
                    {taskDetails._assignedTo?.firstName} {taskDetails._assignedTo?.lastName}
                  </Text>
                </Row>
              </Col>
              <Col span={24}>
                <Row justify={'space-between'}>
                  <Text type={'secondary'} className={'font-size-16 li'}>
                    Due Date
                  </Text>
                  <Text type={'secondary'} className={styles.item_bold}>
                    {moment(taskDetails.dueDate).format('DD.MM.YYYY HH:mm')}
                  </Text>
                </Row>
              </Col>
              <Col span={24}>
                <Row justify={'space-between'}>
                  <Text type={'secondary'} className={styles.item_bold}>
                    Priority
                  </Text>
                  {taskDetails.priorityLevel ? <PriorityTag type={taskDetails.priorityLevel} /> : '-'}
                </Row>
              </Col>
            </Row>
          </Col>
          <Col span={24} className={'margin-t-4'}>
            <Row gutter={[0, 4]}>
              <Col span={24}>
                <Row align={'middle'} className={'gap-4'} wrap={false} justify={'space-between'}>
                  <Text style={{ color: '#99A0AE' }}>
                    Created by <br />
                  </Text>
                  <Text style={{ color: '#99A0AE' }} ellipsis={{ tooltip: true }}>
                    {taskDetails._createdBy?.firstName} {taskDetails._createdBy?.lastName}
                  </Text>
                </Row>
              </Col>
              <Col span={24}>
                <Row className={'gap-4'} wrap={false} align={'middle'} justify={'space-between'}>
                  <Col flex={1}>
                    <Text style={{ color: '#99A0AE' }} ellipsis={{ tooltip: true }}>
                      ID: {taskDetails.uuid}
                    </Text>
                  </Col>
                  <Col>{taskDetails?.status ? <StatusBadge type={taskDetails?.status} /> : ''}</Col>
                </Row>
              </Col>
              <Col span={24}>
                <Row className={'gap-4'} wrap={false} align={'middle'} justify={'space-between'}>
                  <Text style={{ color: '#99A0AE' }}>{taskDetails.formattedCreateDate}</Text>
                  <Text style={{ color: '#99A0AE' }}>{taskDetails.formattedLastModifiedDate}</Text>
                </Row>
              </Col>
            </Row>
          </Col>
          <Col span={24}>
            <HasAccess role={PERMISSIONS.tasks.task.write}>
              <Dropdown
                menu={{
                  items: changeStatusMenu,
                  onClick: ({ key }) => handleStatusChange(key as StatusType),
                }}
                trigger={['click']}
              >
                <Button type={'primary'} block size={'large'} disabled={isFinalStatus}>
                  <Space>
                    Change Status
                    <DownOutlined />
                  </Space>
                </Button>
              </Dropdown>
            </HasAccess>
          </Col>
        </Row>
      </Col>
      <TaskInfoEditModal onCancel={onModalStateChange} open={modalState} id={id} entity={entity} />
    </>
  );
});
